import { EVENT_TEAM_API_BASE_URL } from "../../services/eventTeamApi";
import type { FeedbackForm, Answers } from "./contract";
export interface FormResponse {
  id: string;
  member_uuid: string;
  display_name: string;
  answers: Answers;
  created_at: number;
}
export interface FormShortLink {
  path: string;
  url: string;
  label: string;
  status: "active" | "inactive";
  visits: number;
  created_at: string;
  retired_at: string | null;
}
export interface FormSummary {
  total: number;
  distribution: {
    question_id: string;
    row_label: string | null;
    value: string | number;
    count: number;
  }[];
  daily: { day: string; count: number }[];
  numbers: {
    question_id: string;
    mean: number;
    count: number;
    min: number;
    max: number;
  }[];
}
export async function formsRequest<T>(
  path = "",
  method = "GET",
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${EVENT_TEAM_API_BASE_URL}/forms${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.headers.get("content-type")?.includes("application/json"))
    throw new Error(
      "Your staff session may have expired. Refresh Argus and try again.",
    );
  const data = await response.json();
  if (!response.ok)
    throw new Error(data.error || "Forms are temporarily unavailable.");
  return data;
}
export const formApi = {
  remove: (id: string, revision: number) =>
    formsRequest<{ deleted: boolean }>(
      `/${encodeURIComponent(id)}?revision=${revision}`,
      "DELETE",
    ),
  rewards: (search = "") =>
    formsRequest<{
      awards: { id: number; name: string }[];
      prefixes: { id: number; name: string }[];
    }>(`/reward-options?search=${encodeURIComponent(search)}`),
  links: (id: string) =>
    formsRequest<{ links: FormShortLink[] }>(`/${id}/short-links`),
  createLink: (
    id: string,
    input: { slug: string; label: string; request_id: string },
  ) =>
    formsRequest<{ link: FormShortLink }>(`/${id}/short-links`, "POST", input),
  labelLink: (id: string, path: string, label: string) =>
    formsRequest<{ link: FormShortLink }>(
      `/${id}/short-links/${encodeURIComponent(path)}`,
      "PUT",
      { label },
    ),
  retireLink: (id: string, path: string) =>
    formsRequest<{ link: FormShortLink }>(
      `/${id}/short-links/${encodeURIComponent(path)}`,
      "DELETE",
    ),
  list: (search = "", page = 0, eventId?: string, status = "all") =>
    formsRequest<{ forms: FeedbackForm[]; total: number }>(
      `?${new URLSearchParams({ search, page: String(page), status, ...(eventId ? { event_id: eventId } : {}) })}`,
    ),
  get: (id: string) => formsRequest<FeedbackForm>(`/${encodeURIComponent(id)}`),
  save: (form: FeedbackForm) =>
    formsRequest<FeedbackForm>(`/${encodeURIComponent(form.id)}`, "PUT", form),
  create: (form: Partial<FeedbackForm>) =>
    formsRequest<FeedbackForm>("", "POST", form),
  events: (search = "") =>
    formsRequest<{ events: { id: string; title: string; status: string }[] }>(
      `/events?search=${encodeURIComponent(search)}`,
    ),
  responses: (id: string, search = "", page = 0) =>
    formsRequest<{ responses: FormResponse[]; total: number }>(
      `/${id}/responses?${new URLSearchParams({ search, page: String(page) })}`,
    ),
  summary: (id: string) => formsRequest<FormSummary>(`/${id}/summary`),
  async download(id: string) {
    const r = await fetch(`${EVENT_TEAM_API_BASE_URL}/forms/${id}/export`, {
      credentials: "include",
    });
    if (!r.ok) throw new Error("Export failed. Please try again.");
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = `form-${id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
